'use client'

import { useEffect, useRef } from 'react'

import dynamic from 'next/dynamic'

import { RatingForm, type RatingFormLabels } from './rating-form'
import { RatingPrompt, type RatingPromptLabels } from './rating-prompt'
import { useReviewComposer } from './use-review-composer'

// Product details and account actions only load after the reader submits the inline form.
const ReviewModal = dynamic(() => import('./review-modal').then((m) => m.ReviewModal), { ssr: false })
const Success = dynamic(() => import('./review-flow/success').then((m) => m.Success), { ssr: false })

export interface ReviewComposerLabels extends RatingFormLabels, RatingPromptLabels {}

interface Props {
  variant: 'inline' | 'compact'
  releaseId: number
  releaseName: string
  score: number
  onScore: (score: number) => void
  existingReviewId: number | null
  onSubmitted: (reviewId: number, score: number) => void
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
  labels: ReviewComposerLabels
}

export const ReviewComposer = (props: Props) => {
  const { labels } = props
  const { state, dispatch, submit, closeModal, busy } = useReviewComposer(props)
  const errorMessage =
    state.status === 'ownershipError'
      ? labels.ownershipError
      : state.status === 'error'
        ? labels.errorMessage
        : undefined
  const successHeading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (state.status === 'success') {
      successHeading.current?.focus()
    }
  }, [state.status])

  return (
    <>
      {state.status === 'success' ? (
        <div className="rounded-2xl border border-navy-20 bg-white p-5 sm:p-8">
          <h3 ref={successHeading} tabIndex={-1} className="mb-4 text-[24px] font-semibold text-navy outline-none">
            {labels.successTitle}
          </h3>
          <Success
            releaseName={props.releaseName}
            reviewId={state.reviewId}
            labels={labels}
            onClose={() => dispatch({ type: 'status', status: 'idle' })}
          />
        </div>
      ) : props.variant === 'compact' ? (
        <RatingPrompt
          value={props.score}
          onChange={props.onScore}
          onContinue={() => void submit('compose')}
          busy={busy}
          hasExistingReview={props.existingReviewId != null}
          errorMessage={errorMessage}
          labels={labels}
        />
      ) : (
        <RatingForm
          value={props.score}
          message={state.message}
          onChange={props.onScore}
          onMessage={(message) => dispatch({ type: 'message', message })}
          onContinue={submit}
          busy={busy}
          actionLabel={
            state.status === 'loading'
              ? labels.loadingReviewForm
              : busy
                ? labels.submitting
                : props.existingReviewId != null
                  ? labels.updateYourReview
                  : labels.submit
          }
          errorMessage={errorMessage}
          labels={labels}
        />
      )}
      {props.isModalOpen && state.options && (
        <ReviewModal
          {...state.options}
          isOpen={props.isModalOpen}
          onClose={closeModal}
          releaseId={props.releaseId}
          releaseName={props.releaseName}
          initialScore={props.score}
          initialMessage={state.message}
          existingReviewId={props.existingReviewId}
          labels={labels}
          onSubmitted={props.onSubmitted}
        />
      )}
    </>
  )
}
